import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { clearQrCode } from "@/redux/slices/qrSlice";
import { ArrowLeft, Loader2, Pencil, Check, X, User, Phone, MapPin, Beef } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { getPin } from "@/services/customer";
import { useToast } from "../hooks/use-toast";
import { updateCustomers } from "@/services/auth";

export default function Profile() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { toast } = useToast();
  const token = useSelector((state) => state.auth.token);
    const storedName = localStorage.getItem("name");
    const storedMobile = localStorage.getItem("mobile");

    const { data: customerData, loading } = useSelector((state) => state.customer);

    // ✅ API response: { customer: {...}, walletsDetail: {...} }
    const customer = customerData?.customer;

    const earned = customerData?.walletsDetail?.earnedValue;
    const redeemed = customerData?.walletsDetail?.redeemValue;
    const available = customerData?.walletsDetail?.TotalavailableValue;

    const [editMode, setEditMode] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
const [editData, setEditData] = useState({
    name: "",
    mobile: "",
    noofcatel: "",
    pin: "",
    state: "",
    district: "",
    city: "",
});
    const [editErrors, setEditErrors] = useState({});
    const [pinLoading, setPinLoading] = useState(false);

 const handlePinChange = async (value) => {
    const val = value.replace(/\D/g, "").slice(0, 6);

    setEditData((prev) => ({ ...prev, pin: val }));
    setEditErrors((prev) => ({ ...prev, pin: "" }));

    if (val.length > 0 && val.length < 6) {
        setEditErrors((prev) => ({ ...prev, pin: t("errors.pinInvalid") }));
        return;
    }

    if (val.length !== 6) return;

    // 6 digits complete — API call karo
    setPinLoading(true);
    try {
        const data = await getPin(val);
        if (
            data &&
            data[0] &&
            data[0].Status === "Success" &&
            data[0].PostOffice?.length
        ) {
            const po = data[0].PostOffice[0];
            // ✅ same node names jo postal API response me aate hain
            setEditData((prev) => ({
                ...prev,
                state: po.State || "",
                district: po.District || "",
                city: po.Block || po.Division || "",
            }));
            setEditErrors((prev) => ({ ...prev, pin: "" }));
        } else {
            setEditErrors((prev) => ({ ...prev, pin: t("errors.invalidPin") }));
        }
    } catch {
        setEditErrors((prev) => ({ ...prev, pin: t("errors.pinFetchFail") }));
    } finally {
        setPinLoading(false);
    }
};

    useEffect(() => {
        const customerCode = localStorage.getItem("customercode");
        dispatch(fetchCustomerData(customerCode));
    }, []);

    useEffect(() => {
        if (customer) {
            setEditData({
                name: customer?.name || storedName || "",
                mobile: customer?.mobile || storedMobile || "",
                noofcatel: customer?.noofcatel != null ? String(customer.noofcatel) : "",
                pin: customer?.pin != null ? String(customer.pin) : "",
            });
        }
    }, [customer]);

    const handleEditChange = (field, value) => {
        let val = value;
        if (field === "noofcatel") val = value.replace(/\D/g, "");
        if (field === "pin") val = value.replace(/\D/g, "").slice(0, 6);
        if (field === "mobile") val = value.replace(/\D/g, "").slice(0, 10);
        setEditData((prev) => ({ ...prev, [field]: val }));
        setEditErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validateEdit = () => {
        const errs = {};
        if (!editData.name.trim()) errs.name = t("errors.fullNameRequired");
        if (editData.mobile.length !== 10) errs.mobile = t("errors.mobileInvalid");
        if (editData.pin.length !== 6) errs.pin = t("errors.pinInvalid");
        if (!editData.noofcatel) errs.noofcatel = t("errors.cattleRequired");
        return errs;
    };

    const handleUpdate = async () => {
        // ✅ PIN load ho rahi hai ya error hai to update rok do
        if (pinLoading) return;
        if (editErrors.pin) return;

        const errs = validateEdit();
        if (Object.keys(errs).length > 0) {
            setEditErrors(errs);
            return;
        }
        const customerCode = localStorage.getItem("customercode");
        setUpdateLoading(true);
        try {
           const res = await updateCustomers(token, customerCode, {
    name: editData.name,
    mobile: editData.mobile,
    noofcatel: parseInt(editData.noofcatel),
    pin: parseInt(editData.pin),
    state: editData.state,
    district: editData.district,
    city: editData.city,
});
            if (res && !res.error) {
                toast({
                    title: t("success.profileUpdated") || "Profile updated",
                    description: t("success.profileUpdatedDesc") || "Your details have been saved.",
                });
                localStorage.setItem("name", editData.name);
                localStorage.setItem("mobile", editData.mobile);
                setEditMode(false);
                dispatch(fetchCustomerData(customerCode));
            } else {
                toast({
                    title: t("errors.failed") || "Update failed",
                    description: t("errors.tryAgain") || "Please try again.",
                    variant: "destructive",
                });
            }
        } catch {
            toast({
                title: t("errors.failed") || "Update failed",
                description: t("errors.tryAgain") || "Please try again.",
                variant: "destructive",
            });
        } finally {
            setUpdateLoading(false);
        }
    };

const handleCancelEdit = () => {
    setEditMode(false);
    setEditErrors({});
    setEditData({
        name: customer?.name || storedName || "",
        mobile: customer?.mobile || storedMobile || "",
        noofcatel: customer?.noofcatel != null ? String(customer.noofcatel) : "",
        pin: customer?.pin != null ? String(customer.pin) : "",
        state: customer?.state || "",
        district: customer?.district || "",
        city: customer?.district || "",
    });
};

    const displayName = editMode ? editData.name : (customer?.name || storedName || "");
    const displayMobile = editMode ? editData.mobile : (customer?.mobile || storedMobile || "");


    // ✅ Save button disable condition
    const isUpdateDisabled = updateLoading || pinLoading || !!editErrors.pin;

    const fields = [
        {
            key: "name",
            label: t("profile.fullName") || "Full Name",
            icon: User,
            inputMode: "text",
            maxLength: undefined,
            display: customer?.name || storedName || "-",
        },
        {
            key: "mobile",
            label: t("profile.mobile") || "Mobile",
            icon: Phone,
            inputMode: "numeric",
            maxLength: 10,
            display: customer?.mobile || storedMobile || "-",
        },
        {
            key: "noofcatel",
            label: t("profile.cattle") || "No. of Cattle",
            icon: Beef,
            inputMode: "numeric",
            maxLength: undefined,
            display: customer?.noofcatel != null ? String(customer.noofcatel) : "-",
        },
        {
            key: "pin",
            label: t("profile.pinCode") || "PIN Code",
            icon: MapPin,
            inputMode: "numeric",
            maxLength: 6,
            display: customer?.pin != null ? String(customer.pin) : "-",
        },
    ];

    return (
        <div className="min-h-screen bg-[#F5F6FA] flex justify-center">

            {(loading || updateLoading) && (
                <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
                    <Loader2 className="animate-spin h-12 w-12" style={{ color: "#1A1F36" }} />
                </div>
            )}

            <div className="w-full max-w-md bg-white min-h-screen md:rounded-3xl md:shadow-lg flex flex-col">

                {/* Header */}
                <div className="relative flex items-center justify-center border-b border-gray-100 px-4 py-4 shrink-0">
                    <button
                        onClick={() => navigate(-1)}
                        className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full"
                        style={{ backgroundColor: "#1A1F3610" }}
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1A1F36]" />
                    </button>
                    <p className="text-base font-semibold text-[#1A1F36]">
                        {editMode ? (t("profile.editTitle") || "Edit Profile") : t("profile.title")}
                    </p>
                    {!editMode && (
                        <button
                            onClick={() => setEditMode(true)}
                            className="absolute right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                            style={{ backgroundColor: "#1A1F3610", color: "#1A1F36" }}
                        >
                            <Pencil className="w-3 h-3" />
                            {t("common.edit") || "Edit"}
                        </button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3">

                    {/* Avatar + Stats Card — dark gradient */}
                    <div
                        className="rounded-2xl px-5 py-7 flex flex-col items-center"
                        style={{ background: "linear-gradient(135deg, #1A1F36 0%, #2d3555 100%)" }}
                    >
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mb-4"
                            style={{
                                background: "rgba(255,255,255,0.13)",
                                border: "2px solid rgba(255,255,255,0.25)",
                                color: "#fff",
                            }}
                        >
                            {(displayName || "U")[0].toUpperCase()}
                        </div>
                        <p className="text-lg font-bold text-white">{displayName || storedName}</p>
                        <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>
                        {displayMobile || storedMobile}
                        </p>

                        {/* Stats row */}
                        <div className="w-full mt-5 rounded-xl grid grid-cols-3" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            {[
                                { label: t("profile.earned") || "Earned", value: `₹${earned ?? 0}` },
                                { label: t("profile.redeemed") || "Redeemed", value: `₹${redeemed ?? 0}` },
                                { label: t("profile.available") || "Available", value: `₹${available ?? 0}` },
                            ].map((stat, i) => (
                                <div
                                    key={i}
                                    className="px-2 py-3 text-center"
                                    style={{ borderRight: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none" }}
                                >
                                    <p className="text-[9px] uppercase tracking-widest mb-1" style={{ color: "rgba(255,255,255,0.4)" }}>
                                        {stat.label}
                                    </p>
                                    <p className="text-sm font-bold text-white">{stat.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* View Mode */}
                    {!editMode ? (
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            {fields.map((field, i) => {
                                const Icon = field.icon;
                                return (
                                    <div key={field.key}>
                                        <div className="flex items-center gap-3 px-4 py-4">
                                            <div
                                                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                                style={{ backgroundColor: "#1A1F3608" }}
                                            >
                                                <Icon className="w-4 h-4" style={{ color: "#1A1F36" }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                                    {field.label}
                                                </p>
                                                <p className="text-sm font-semibold truncate" style={{ color: "#1A1F36" }}>
                                                    {field.display}
                                                </p>
                                            </div>
                                        </div>
                                        {i < fields.length - 1 && (
                                            <div className="h-px mx-4" style={{ backgroundColor: "#f1f1f1" }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* Edit Mode */
                        <div className="space-y-3">
                            <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4 space-y-4">
                                {fields.map((field) => {
                                    const Icon = field.icon;
                                    const hasErr = !!editErrors[field.key];
                                    return (
                                        <div key={field.key}>
                                            <label
                                                className="text-[11px] font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
                                                style={{ color: "#1A1F3680" }}
                                            >
                                                <Icon className="w-3 h-3" />
                                                {field.label}
                                                {/* ✅ PIN loading indicator */}
                                                {field.key === "pin" && pinLoading && (
                                                    <span className="ml-1 text-yellow-600 normal-case font-normal text-[10px] flex items-center gap-1">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        {t("signup.fetchingLocation")}
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                value={editData[field.key]}
                                                onChange={(e) =>
                                                    field.key === "pin"
                                                        ? handlePinChange(e.target.value)
                                                        : handleEditChange(field.key, e.target.value)
                                                }
                                                inputMode={field.inputMode}
                                                maxLength={field.maxLength}
                                                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all font-medium"
                                                style={{
                                                    border: hasErr ? "1.5px solid #f87171" : "1.5px solid #1A1F3620",
                                                    background: hasErr ? "#fff5f5" : "#f9f9fb",
                                                    color: hasErr ? "#b91c1c" : "#1A1F36",
                                                }}
                                            />
                                            {hasErr && (
                                                <p className="text-[11px] text-red-500 mt-1 ml-1">
                                                    {editErrors[field.key]}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Cancel + Save */}
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCancelEdit}
                                    className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
                                    style={{ border: "1.5px solid #1A1F3620", color: "#1A1F36", background: "#fff" }}
                                >
                                    {t("common.cancel") || "Cancel"}
                                </button>
                                <button
                                    onClick={handleUpdate}
                                    disabled={isUpdateDisabled}
                                    className="flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    style={{ backgroundColor: "#1A1F36" }}
                                >
                                    {pinLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {t("signup.fetchingLocation")}
                                        </>
                                    ) : updateLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {t("common.pleaseWait") || "Saving..."}
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {t("common.save") || "Save Changes"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Logout — view mode only */}
                    {!editMode && (
                        <button
                            onClick={() => {
                                dispatch(clearQrCode());
                                dispatch(logout());
                            }}
                             className="w-full py-3.5 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-transform"
                            style={{ backgroundColor: '#1A1F36' }}
                        >
                            {t("profile.logout")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}